import type { ReactNode } from 'react';
import { useLayoutEffect, useMemo, useState, memo, useEffect } from 'react';
import { useMatches, useNavigate } from 'react-router';

import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/ar';

import i18n, { locale } from '@/i18n';
import { useMsal } from '@azure/msal-react';
import { useSearchParams } from 'react-router';

import I18nContext from './i18n.context';

function I18nProvider({ children }: { children: ReactNode }) {
  const [{ params }] = useMatches();
  const navigate = useNavigate();
  const [currentLocale, setLocale] = useState(locale);
  const { instance, accounts, inProgress } = useMsal();
  const [searchParams] = useSearchParams();

  useLayoutEffect(() => {
    i18n.changeLanguage(currentLocale).then(() => {
      document
        .getElementsByTagName('html')[0]
        .setAttribute('dir', i18n.dir(currentLocale));

      dayjs.locale(currentLocale);
    });
  }, [currentLocale, params]);

  useEffect(() => {
    const allSearchParams = searchParams.toString();
    if (!params.lng && (!location.hash || inProgress === 'none')) {
      navigate(
        `/${currentLocale}${allSearchParams ? `?${allSearchParams}` : ''}`
      );
    }
  }, [
    accounts,
    currentLocale,
    inProgress,
    instance,
    navigate,
    params.lng,
    searchParams,
  ]);

  const providerValue = useMemo(
    () => ({
      locale: currentLocale,
      setLocale,
    }),
    [currentLocale]
  );

  return (
    <I18nContext.Provider value={providerValue}>
      {children}
    </I18nContext.Provider>
  );
}

const I18nProviderMemo = memo(I18nProvider);
I18nProviderMemo.displayName = 'I18nProvider';

export default I18nProviderMemo;
